import type { ProjectsListResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { AsOfPill } from '@/components/pulse/day';
import { ArrowIcon } from '@/components/pulse/icons';
import { PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { WorkloadBar, WorkloadLegend } from '@/components/pulse/workload';
import { useProjectsList } from '@/domains/projects/api';
import { Meter } from '@/domains/projects/project-milestones-page';
import { useShell } from '@/domains/shell/api';

const CHIP: Readonly<Record<string, string>> = { 'On track': 'c-merged', 'At risk': 'c-warn', Late: 'c-changes', Done: 'c-approved', Closed: 'c-kind' };

/** The list's two tabs (PRJ-7): All projects, and Meetings we follow (MT-1). */
export function ProjectsListTabs({ view }: { readonly view: 'all' | 'meetings' }): React.ReactElement {
  return (
    <div className="tabsbar">
      <nav className="tabs" aria-label="Projects view">
        <Link className="tab" to="/projects" aria-current={view === 'all' ? 'page' : undefined}>
          All projects
        </Link>
        <Link className="tab" to="/projects/meetings" aria-current={view === 'meetings' ? 'page' : undefined}>
          Meetings we follow
        </Link>
      </nav>
    </div>
  );
}

/** Projects (PRJ-7), built from the prototype's `viewProjects()`: every project, laid out as People. */
export function ProjectsListPage(): React.ReactElement {
  const page = useProjectsList();
  const shell = useShell();
  return (
    <>
      <PageHeader
        eyebrow={`Projects · ${page.data?.weekLabel ?? ''}`}
        title="Projects"
        sub="Every project's milestones, open work and week. Open one for its board, milestones and meetings."
        right={<AsOfPill label={shell.data?.freshness.asOfLabel ?? null} />}
      />
      {page.data !== undefined ? <ProjectsListView data={page.data} /> : null}
    </>
  );
}

export function ProjectsListView({ data }: { readonly data: ProjectsListResponse }): React.ReactElement {
  return (
    <>
      <ProjectsListTabs view="all" />
      <section className="card pad stack" style={{ gap: 12 }} aria-labelledby="owh">
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h2 id="owh">Open work</h2>
          <WorkloadLegend />
        </div>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>
          {data.recorded ? "Open stories, Teamwork tasks and issues in each project, and its next milestone. Bars share one scale." : 'Not collected yet.'}
        </span>
        <div>
          {data.projects.map((project) => (
            <div key={project.slug} className="wl-row">
              <Link to={`/projects/${project.slug}`} className="stack" style={{ textDecoration: 'none', color: 'var(--ink)', minWidth: 0 }}>
                <span style={{ fontWeight: 600 }}>{project.name}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {project.nextMilestone === null ? 'No milestone yet' : `Next: ${project.nextMilestone.title}, ${project.nextMilestone.dateLabel}`}
                </span>
              </Link>
              {project.open.total > 0 ? <WorkloadBar summary={{ ...project.open, sources: '' }} scale={data.scale} /> : <span style={{ fontSize: 13, color: 'var(--muted)' }}>Nothing open</span>}
              <span className="r num" style={{ fontWeight: 600 }}>
                {project.open.total}
              </span>
            </div>
          ))}
        </div>
      </section>
      <div className="people">
        {data.projects.map((project) => (
          <ProjectCard key={project.slug} project={project} />
        ))}
      </div>
    </>
  );
}

function ProjectCard({ project }: { readonly project: ProjectsListResponse['projects'][number] }): React.ReactElement {
  const next = project.nextMilestone;
  return (
    <article className="card pcard">
      <div className="row" style={{ gap: 12 }}>
        <div className="stack" style={{ gap: 2, flexGrow: 1, minWidth: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>{project.name}</span>
          <span className="sub" style={{ fontSize: 13 }}>
            {project.source}
          </span>
        </div>
        <Link className="linkish" to={`/projects/${project.slug}`} style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
          Open project <ArrowIcon />
        </Link>
      </div>
      <div className="pstats num">
        <div className="pstat">
          <b>{project.builtThisWeek}</b>
          <span>Built this week</span>
        </div>
        <div className="pstat">
          <b>{project.toProductionThisWeek}</b>
          <span>To production</span>
        </div>
        <div className="pstat">
          <b style={{ color: project.atRisk > 0 ? 'var(--warn-ink)' : 'var(--ink)' }}>{project.atRisk}</b>
          <span>Items at risk</span>
        </div>
      </div>
      {next !== null ? (
        <div className="stack" style={{ gap: 6 }}>
          <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
            <span>
              <b style={{ fontWeight: 600 }}>{next.title}</b> <span style={{ color: 'var(--muted)' }}>· {next.dateLabel}</span>
            </span>
            <span className={`chip ${CHIP[next.state] ?? 'c-kind'}`}>{next.state}</span>
          </div>
          <Meter done={next.done} total={next.total} state={next.state} />
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            {next.done} of {next.total} done · forecast {next.forecastLabel}
          </span>
        </div>
      ) : (
        <span style={{ color: 'var(--muted)' }}>
          No milestone yet. <Link to={`/projects/${project.slug}/milestones`}>Add one</Link>
        </span>
      )}
      {project.latestBuilt.length > 0 ? (
        <ul className="plist">
          {project.latestBuilt.map((item) => (
            <li key={item.ref}>
              <SourceLink href={item.url} style={{ color: 'inherit', textDecoration: 'none' }}>
                {item.title}
              </SourceLink>
            </li>
          ))}
        </ul>
      ) : (
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>Nothing built on it this week.</span>
      )}
      {project.people.length > 0 ? (
        <div className="row" style={{ gap: 8, fontSize: 13, color: 'var(--muted)' }}>
          <span className="row" style={{ gap: 4 }}>
            {project.people.map((person) => (
              <span key={person.userId} className="avatar sm" title={person.name} style={{ background: person.avatarColour, width: 24, height: 24, fontSize: 10 }}>
                {person.initials}
              </span>
            ))}
          </span>
          <span>{project.people.map((person) => person.name.split(' ')[0]).join(', ')}</span>
        </div>
      ) : null}
    </article>
  );
}

